import { z } from 'zod';
import { ModelProviderSchema } from './model.js';
// ─── Eval cost estimation ────────────────────────────────────────────────────
/**
 * Request shape for `GET /v1/evaluations/estimate-cost`.
 * Exactly one of `suiteId` | `taskIds` selects the Tasks to estimate.
 */
export const EvalCostEstimateRequestSchema = z
    .object({
    workflowId: z.string().min(1),
    /** Estimate running every Task in this Suite. Mutually exclusive with `taskIds`. */
    suiteId: z.string().min(1).optional(),
    /** Estimate running these specific Tasks. Mutually exclusive with `suiteId`. */
    taskIds: z.array(z.string().min(1)).min(1).optional(),
    /** Trials per Task (floored per-task by `Task.minTrials`). Default 1. */
    nTrials: z.number().int().positive().max(1000).optional(),
    /** When provided, the recent-runs fallback prefers runs pinned to this version. */
    workflowVersionId: z.string().min(1).optional(),
})
    .refine((v) => (v.suiteId === undefined) !== (v.taskIds === undefined), {
    message: 'Provide exactly one of suiteId or taskIds',
});
/**
 * Which historical signal produced the per-trial cost, most → least specific.
 * - `prior_suite_evals`     — completed Evaluations of the same workflow + suite (includes judge cost)
 * - `prior_workflow_evals`  — completed Evaluations of the same workflow, any suite (includes judge cost)
 * - `recent_runs`           — recent completed Runs of the workflow (EXCLUDES judge/grader cost)
 * - `none`                  — no historical cost data; estimate is null
 */
export const EvalCostEstimateBasisSchema = z.enum([
    'prior_suite_evals',
    'prior_workflow_evals',
    'recent_runs',
    'none',
]);
/** A point estimate with a low/high band (p50 / p10 / p90 of the samples). */
export const CostBandUsdSchema = z.object({
    estimate: z.number().nonnegative(),
    low: z.number().nonnegative(),
    high: z.number().nonnegative(),
});
/** Response shape for `GET /v1/evaluations/estimate-cost`. */
export const EvalCostEstimateSchema = z.object({
    workflowId: z.string(),
    suiteId: z.string().nullable(),
    taskCount: z.number().int().nonnegative(),
    /** Σ over tasks of max(task.minTrials ?? 1, nTrials ?? 1, 1). */
    totalTrials: z.number().int().nonnegative(),
    /** Null when basis is `none`. */
    perTrialCostUsd: CostBandUsdSchema.nullable(),
    /** perTrialCostUsd × totalTrials. Null when basis is `none`. */
    estimatedTotalUsd: CostBandUsdSchema.nullable(),
    basis: EvalCostEstimateBasisSchema,
    /** Number of historical data points behind the estimate. */
    sampleSize: z.number().int().nonnegative(),
    /** ISO timestamps of the newest/oldest sample used. Null when basis is `none`. */
    newestSampleAt: z.string().nullable(),
    oldestSampleAt: z.string().nullable(),
    /** False for `recent_runs` — Run.totalCostUsd does not include grader/judge LLM cost. */
    includesJudgeCosts: z.boolean(),
    warnings: z.array(z.string()),
});
// ─── Workflow-level eval cost estimate ──────────────────────────────────────
/**
 * Which historical signal produced the workflow-level estimate.
 * - `prior_workflow_evals` — completed Evaluations of the workflow (includes judge cost;
 *                            yields a full-eval total band + per-trial band)
 * - `recent_runs`          — recent completed Runs (per-trial band only — the eval
 *                            composition is unknown, so no total; EXCLUDES judge cost)
 * - `none`                 — no historical cost data; all estimates are null
 */
export const WorkflowEvalEstimateBasisSchema = z.enum([
    'prior_workflow_evals',
    'recent_runs',
    'none',
]);
/**
 * Response shape for `GET /v1/workflows/:id/eval-estimate` — "what would
 * running an evaluation of this workflow cost?", derived purely from the
 * workflow's history (no suite/task input required).
 */
export const WorkflowEvalEstimateSchema = z.object({
    workflowId: z.string(),
    basis: WorkflowEvalEstimateBasisSchema,
    /** Cost per trial (= per Run). Null when basis is `none`. */
    perTrialCostUsd: CostBandUsdSchema.nullable(),
    /** Cost of a full eval, banded over prior evals' totals. Null unless basis
     *  is `prior_workflow_evals` (run history alone can't size a whole eval). */
    estimatedTotalUsd: CostBandUsdSchema.nullable(),
    /** Median number of Runs (trials) per sampled prior eval. Null when no prior evals. */
    typicalTrials: z.number().int().nonnegative().nullable(),
    sampleSize: z.number().int().nonnegative(),
    newestSampleAt: z.string().nullable(),
    oldestSampleAt: z.string().nullable(),
    /** False for `recent_runs` — Run.totalCostUsd does not include grader/judge LLM cost. */
    includesJudgeCosts: z.boolean(),
    warnings: z.array(z.string()),
});
// ─── Per-run cost breakdown ─────────────────────────────────────────────────
/**
 * One row of `GET /v1/runs/:id/costs` — CostRecord rows aggregated by
 * nodeExecutionId and joined to the owning WorkflowNode.
 *
 * `nodeKey` is `'(unattributed)'` for records whose nodeExecution is null
 * (run-level usage with no owning node). That is a real bucket, not a
 * missing value.
 */
export const RunPerNodeCostSchema = z.object({
    nodeKey: z.string(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    usd: z.number().nonnegative(),
    calls: z.number().int().nonnegative(),
});
/**
 * Response shape for `GET /v1/runs/:id/costs`.
 *
 * This schema exists because its absence shipped a bug: the console
 * hand-rolled `{ nodeId, costUsd }` / `totalUsd` against a service that
 * returns `{ nodeKey, …, usd }` with the total at `totals.usd`, so the cost
 * table rendered a row per node with every cell undefined and the KPI grid's
 * cost fallback was permanently null. Nothing typed the boundary, so nothing
 * caught it. Both sides now infer from here. (DEV-17082.)
 */
export const RunCostsResponseSchema = z.object({
    runId: z.string(),
    totals: z.object({
        tokensIn: z.number().int().nonnegative(),
        tokensOut: z.number().int().nonnegative(),
        usd: z.number().nonnegative(),
    }),
    perNode: z.array(RunPerNodeCostSchema),
});
export const CostRecordSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    runId: z.string().nullable(),
    nodeExecutionId: z.string().nullable(),
    evaluationId: z.string().nullable(),
    provider: ModelProviderSchema,
    modelId: z.string(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    cacheWriteTokens: z.number().int().nonnegative(),
    usd: z.number().nonnegative(),
    createdAt: z.date(),
});
//# sourceMappingURL=cost.js.map